"use client";

import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { cn } from "@/lib/utils";
import { Store, User, Tag, ChevronDown, ChevronUp, Filter } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";
import PermissionGuard from "@/components/custom/common/PermissionGuard";

const presets = [
    { label: "Today", getValue: () => [moment().startOf("day"), moment().endOf("day")] },
    { label: "Yesterday", getValue: () => [moment().subtract(1, "days").startOf("day"), moment().subtract(1, "days").endOf("day")] },
    { label: "This Week", getValue: () => [moment().startOf("week"), moment().endOf("week")] },
    { label: "Last Week", getValue: () => [moment().subtract(1, "weeks").startOf("week"), moment().subtract(1, "weeks").endOf("week")] },
    { label: "This Month", getValue: () => [moment().startOf("month"), moment().endOf("month")] },
    { label: "Last Month", getValue: () => [moment().subtract(1, "months").startOf("month"), moment().subtract(1, "months").endOf("month")] },
    { label: "This Year", getValue: () => [moment().startOf("year"), moment().endOf("year")] },
];

export default function DashboardFilter({
    startDate,
    endDate,
    onFilterChange,
    // New filter props
    shops = [],
    users = [],
    categories = [],
    shopId = "",
    userId = "",
    categoryId = "",
    onShopChange,
    onUserChange,
    onCategoryChange,
    showEntityFilters = true,
    defaultExpanded = false
}) {
    const [selectedPreset, setSelectedPreset] = useState("Today");
    const [customRange, setCustomRange] = useState(false);
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    const handlePresetClick = (preset) => {
        const [start, end] = preset.getValue();
        setSelectedPreset(preset.label);
        setCustomRange(false);
        onFilterChange(start.toDate(), end.toDate());
    };

    const handleDateChange = (type, date) => {
        setCustomRange(true);
        setSelectedPreset("Custom");

        if (type === "start") {
            onFilterChange(date, endDate);
        } else {
            onFilterChange(startDate, date);
        }
    };

    // Count active filters
    const activeFilterCount = [shopId, userId, categoryId].filter(Boolean).length;

    return (
        <Collapsible open={isExpanded} onOpenChange={setIsExpanded} className="w-fit">
            {/* Collapsed View - Show summary and toggle */}
            <div className="flex items-center gap-2 bg-white dark:bg-slate-900 p-2 border shadow-sm">
                <span className="text-xs font-medium text-muted-foreground hidden sm:inline">
                    {selectedPreset}: {moment(startDate).format("MMM D")} - {moment(endDate).format("MMM D")}
                </span>
                {activeFilterCount > 0 && (
                    <span className="text-xs bg-primary text-white px-1.5 py-0.5 rounded-full">{activeFilterCount}</span>
                )}
                <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-7 px-2 gap-1">
                        <Filter className="h-3 w-3" />
                        <span className="text-xs">Filters</span>
                        {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </Button>
                </CollapsibleTrigger>
            </div>

            {/* Expanded View */}
            <CollapsibleContent className="bg-white dark:bg-slate-900 border border-t-0 shadow-sm p-3 space-y-3">
                {/* Date Filters Row */}
                <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
                    {/* Presets */}
                    <div className="flex flex-wrap gap-1.5">
                        {presets.map((preset) => (
                            <Button
                                key={preset.label}
                                variant={selectedPreset === preset.label ? "default" : "outline"}
                                size="sm"
                                onClick={() => handlePresetClick(preset)}
                                className="text-xs h-7 px-2"
                            >
                                {preset.label}
                            </Button>
                        ))}
                    </div>

                    <div className="h-4 w-px bg-gray-200 hidden md:block" />

                    {/* Custom Date Pickers */}
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <DatePicker
                                selected={startDate}
                                onChange={(date) => handleDateChange("start", date)}
                                selectsStart
                                startDate={startDate}
                                endDate={endDate}
                                className={cn(
                                    "w-[100px] text-xs p-1.5 border rounded cursor-pointer",
                                    customRange ? "border-primary ring-1 ring-primary" : "border-gray-200 dark:border-slate-700"
                                )}
                            />
                        </div>
                        <span className="text-gray-400 dark:text-gray-500 text-xs">-</span>
                        <div className="relative">
                            <DatePicker
                                selected={endDate}
                                onChange={(date) => handleDateChange("end", date)}
                                selectsEnd
                                startDate={startDate}
                                endDate={endDate}
                                minDate={startDate}
                                className={cn(
                                    "w-[100px] text-xs p-1.5 border rounded cursor-pointer",
                                    customRange ? "border-primary ring-1 ring-primary" : "border-gray-200 dark:border-slate-700"
                                )}
                            />
                        </div>
                    </div>
                </div>

                {/* Entity Filters Row (Shop, User, Category) */}
                {showEntityFilters && (shops.length > 0 || users.length > 0 || categories.length > 0) && (
                    <div className="flex flex-wrap gap-2 items-center border-t pt-3">
                        {/* Shop Filter */}
                        {shops.length > 0 && onShopChange && (
                            <Select value={shopId || "all"} onValueChange={(val) => onShopChange(val === "all" ? "" : val)}>
                                <SelectTrigger className="w-[130px] h-8 text-xs">
                                    <Store className="h-3 w-3 mr-1.5 text-muted-foreground" />
                                    <SelectValue placeholder="All Shops" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Shops</SelectItem>
                                    {shops.map((shop) => (
                                        <SelectItem key={shop.id} value={shop.id.toString()}>
                                            {shop.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}

                        {/* User Filter (with ViewAllSales permission) */}
                        {users.length > 0 && onUserChange && (
                            <PermissionGuard required="ViewAllSales">
                                <Select value={userId || "all"} onValueChange={(val) => onUserChange(val === "all" ? "" : val)}>
                                    <SelectTrigger className="w-[130px] h-8 text-xs">
                                        <User className="h-3 w-3 mr-1.5 text-muted-foreground" />
                                        <SelectValue placeholder="All Users" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Users</SelectItem>
                                        {users.map((user) => (
                                            <SelectItem key={user.id} value={user.id.toString()}>
                                                {user.username || `${user.first_name} ${user.last_name}`}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </PermissionGuard>
                        )}

                        {/* Category Filter */}
                        {categories.length > 0 && onCategoryChange && (
                            <Select value={categoryId || "all"} onValueChange={(val) => onCategoryChange(val === "all" ? "" : val)}>
                                <SelectTrigger className="w-[130px] h-8 text-xs">
                                    <Tag className="h-3 w-3 mr-1.5 text-muted-foreground" />
                                    <SelectValue placeholder="All Categories" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Categories</SelectItem>
                                    {categories.map((cat) => (
                                        <SelectItem key={cat.id} value={cat.id.toString()}>
                                            {cat.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    </div>
                )}
            </CollapsibleContent>
        </Collapsible>
    );
}
